import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectorRef,
    ViewChild,
    inject,
    signal,
    computed,
} from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { EuiAppShellService, EuiGrowlService, UserState } from '@eui/core';
import { UrlService } from 'src/app/services/url.service';
import { PermissionActions } from './app.permission.actions';
import { Subscription, of } from 'rxjs';
import { filter, take, catchError, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppAuthService } from './app-auth.service';
import { AppStarterService } from './app-starter.service';
import { environment } from './../environments/environment';
import { AppVisibilityService } from './app-visibility.service';
import { WebtoolsService } from './webtools.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { AnnouncementModalComponent } from '@shared/announcements/announcement-modal.component';
import { System } from 'src/app/features/notices/models/enums';
import { NoticeApiService } from 'src/app/features/notices/services/notice-api.service';
import { Notice } from './features/notices/models/payload';
import { Config, Configuration } from './app-config.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from './shared/shared.module';
import { EUI_LANGUAGE_SELECTOR } from '@eui/components/eui-language-selector';
import { EUI_USER_PROFILE } from '@eui/components/eui-user-profile';
import { EUI_ICON } from '@eui/components/eui-icon';
import { EUI_LAYOUT } from '@eui/components/layout';
import { EUI_BREADCRUMB } from '@eui/components/eui-breadcrumb';

declare const $wt;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: true,
    imports: [
        SharedModule,
        TranslateModule,
        ...EUI_LAYOUT,
        ...EUI_ICON,
        ...EUI_USER_PROFILE,
        ...EUI_LANGUAGE_SELECTOR,
        ...EUI_BREADCRUMB,
    ],
})
export class AppComponent implements OnInit, OnDestroy {
    private asService = inject(EuiAppShellService);
    private growlService = inject(EuiGrowlService);
    private appStarterService = inject(AppStarterService);
    private appAuthService = inject(AppAuthService);
    private visibilityService = inject(AppVisibilityService);
    private webtoolsService = inject(WebtoolsService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private urlService = inject(UrlService);
    private breadcrumbService = inject(BreadcrumbService);
    private noticeApiService = inject<NoticeApiService<Notice>>(NoticeApiService);
    private cdr = inject(ChangeDetectorRef);
    private translateService = inject(TranslateService);
    private titleService = inject(Title);

    @ViewChild(AnnouncementModalComponent)
    announcementModal: AnnouncementModalComponent;

    breadcrumbs$ = this.breadcrumbService.breadcrumbs$;
    environment = environment;

    userInfos: UserState;
    logged = false;

    showFooter = false;
    showHeader = false;
    showToolBar = false;
    showHelp = false;

    isMobile: boolean;

    loginLink: string;
    logoutLink: string;

    showLogOutIn = false;

    announcements = signal<Notice[]>([]);
    currentAlertIndex = 0;

    menuItems = signal<object[]>([]);
    sideBarMenuItems = signal<object[]>([]);
    readonly currentLang = toSignal(this.translateService.onLangChange.pipe(map((e) => e.lang)), {
        initialValue: this.translateService.currentLang,
    });
    /** Empty until items are ready (prevents EUI caching an empty state on first mount).
     *  Changes value on language switch (forces EUI component remount so submenus retranslate). */
    readonly menuRenderKeys = computed(() => (this.menuItems().length ? [this.currentLang()] : []));

    config: Config;
    configuration: Configuration;

    isViewReferenceAvailable = false;
    isViewMasterAvailable = false;
    isViewTranslationAvailable = false;
    isViewCatalogueAvailable = false;
    isOpenCatalogueManagementConsoleAvailable = false;
    isOperatorMenuAvailable = false;
    isLaboratoryMenuAvailable = false;
    isAlertMenuAvailable = false;
    isConsumerLinkMenuAvailable = false;

    subs: Subscription[] = [];

    private loginUrl = './backend/logged/login?url=';
    private logoutUrl = './backend/logged/logout?url=';

    constructor() {
        this.urlService.setUrlListener();
        this.setLink();
        this.subs.push(
            this.router.events
                .pipe(filter((event: Event) => event instanceof NavigationEnd))
                .subscribe(() => this.setLink())
        );
    }

    ngOnInit() {
        this.asService.sidebarToggle();

        this.subs.push(
            this.visibilityService.showOptions$.subscribe(
                ({ showFooter, showHeader, showToolBar, showHelp }) => {
                    this.showFooter = showFooter;
                    this.showHeader = showHeader;
                    this.showToolBar = showToolBar;
                    this.showHelp = showHelp;
                    this.cdr.detectChanges();
                }
            )
        );

        this.subs.push(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => {
                    const hideHeaderFooter =
                        this.route.snapshot.queryParams['hideHeaderFooter'] === 'true';
                    this.visibilityService.setOptions(
                        !hideHeaderFooter,
                        !hideHeaderFooter,
                        !hideHeaderFooter,
                        !hideHeaderFooter
                    );
                    this.updatePageTitleFromRoute();
                })
        );

        this.subs.push(
            this.appStarterService.getStartupData().subscribe(([config, configuration, user]) => {
                this.userInfos = user;
                this.config = config;
                this.configuration = configuration;

                this.logged = !!this.userInfos?.fullName;

                this.isViewReferenceAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.ViewReferenceDataItem,
                ]);
                this.isViewMasterAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.ViewMasterDataItem,
                ]);
                this.isViewTranslationAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.ViewReferenceDataItemTranslation,
                ]);
                this.isViewCatalogueAvailable =
                    this.isViewReferenceAvailable ||
                    this.isViewMasterAvailable ||
                    this.isViewTranslationAvailable;
                this.isOpenCatalogueManagementConsoleAvailable =
                    this.appAuthService.checkSecurityAction([
                        PermissionActions.OpenCatalogueManagementConsole,
                    ]);
                this.isOperatorMenuAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.SearchOperatorItem,
                ]);
                this.isLaboratoryMenuAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.SearchLaboratoryItem,
                ]);
                this.isAlertMenuAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.CreateAlertBanner,
                ]);
                this.isConsumerLinkMenuAvailable = this.appAuthService.checkSecurityAction([
                    PermissionActions.CreateConsumerLink,
                ]);

                this.buildMenus();

                this._checkSessionExpired();
                if (this.logged) {
                    this.getAnnouncements();
                }

                this.renderWebTools(config);
                this.updatePageTitleFromRoute();
            })
        );

        this.subs.push(
            this.asService.breakpoints$.subscribe((state) => {
                this.isMobile = state.isMobile;
                this.createSideBarMenu();
            })
        );

        // Reactively update menus when language changes
        this.subs.push(
            this.translateService.onLangChange.subscribe((event) => {
                this.buildMenus();
                this.updatePageTitleFromRoute();
                if (event?.lang) {
                    localStorage.setItem('i18n', JSON.stringify({ activeLang: event.lang }));
                }
            })
        );
    }

    toggleLog() {
        if (this.logged) {
            window.location.href = this.logoutLink;
        } else {
            window.location.href = this.loginLink;
        }
    }

    ngOnDestroy() {
        this.subs.forEach((s) => s.unsubscribe());
    }

    private buildMenus() {
        const menuItems = [
            {
                label: this.translateService.instant('toolbar.search'),
                url: 'screen/search',
                isHome: true,
                iconSvgName: 'eui-home',
            },
            {
                label: this.translateService.instant('toolbar.consumers'),
                url: 'screen/consumers',
            },
        ];

        const nextMenuItems = this.appendMenus(menuItems);

        this.menuItems.set(nextMenuItems);
        this.createSideBarMenu(nextMenuItems);
    }

    private appendMenus(menuItems: object[]) {
        if (!this.logged || !this.isOpenCatalogueManagementConsoleAvailable) return menuItems;

        const catalogueMenu = {
            label: this.translateService.instant('toolbar.catalogues'),
            children: [],
        };
        let supportMenu;
        if (this.isConsumerLinkMenuAvailable || this.isAlertMenuAvailable) {
            supportMenu = {
                label: this.translateService.instant('toolbar.support'),
                children: [],
            };
            if (this.isAlertMenuAvailable) {
                supportMenu.children.push(
                    this.createMenuItem(
                        this.translateService.instant('toolbar.notices'),
                        'screen/logged/notices',
                        'eui-search'
                    )
                );
            }
            if (this.isConsumerLinkMenuAvailable) {
                supportMenu.children.push(
                    this.createMenuItem(
                        this.translateService.instant('toolbar.consumerLink'),
                        'screen/logged/consumer-link',
                        'eui-search'
                    )
                );
            }
        }

        if (this.isOperatorMenuAvailable) {
            catalogueMenu.children.push(
                this.createMenuItem(
                    this.translateService.instant('toolbar.operator'),
                    'screen/logged/operators',
                    'eui-search'
                )
            );
        }
        if (this.isLaboratoryMenuAvailable) {
            catalogueMenu.children.push(
                this.createMenuItem(
                    this.translateService.instant('toolbar.laboratories'),
                    'screen/logged/laboratories',
                    'eui-search'
                )
            );
        }

        if (this.isViewCatalogueAvailable) {
            catalogueMenu.children.push(
                this.createMenuItem(
                    this.translateService.instant('toolbar.view.catalogues'),
                    'screen/logged/catalogues/list',
                    'eui-search'
                )
            );
        }

        return [...menuItems, catalogueMenu, supportMenu].filter(Boolean);
    }

    private createSideBarMenu(menuItems = this.menuItems()) {
        if (!this.config || !this.configuration) {
            return;
        }

        const sideBarMenuItems = this.isMobile ? [...menuItems] : [];

        if (this.logged) {
            sideBarMenuItems.push({
                label: 'IRASFF',
                urlExternal: this.config.irasffUrl,
                urlExternalTarget: '_blank',
            });
        }
        sideBarMenuItems.push(
            {
                label: 'TRACES',
                urlExternal: this.config.tntUrl,
                urlExternalTarget: '_blank',
            },
            {
                label: this.translateService.instant('toolbar.more.tools'),
                children: [
                    ...(this.logged
                        ? [
                              {
                                  label: 'QlikSense',
                                  urlExternal: this.config.qliksenseUrl,
                                  urlExternalTarget: '_blank',
                              },
                          ]
                        : []),
                    {
                        label: 'Open Data API',
                        urlExternal: this.configuration.openPortalLink,
                        urlExternalTarget: '_blank',
                    },
                ],
            },
            {
                label: this.translateService.instant('toolbar.help'),
                children: [
                    {
                        label: this.translateService.instant('toolbar.user.manual'),
                        urlExternal:
                            'https://webgate.ec.europa.eu/IMSOC/rasff-window-help/index.html',
                        urlExternalTarget: '_blank',
                    },
                    {
                        label: this.translateService.instant('toolbar.contactSupport'),
                        url: '/screen/support',
                    },
                    {
                        label: this.translateService.instant('toolbar.feedback'),
                        url: '/screen/feedback',
                    },
                    {
                        label: this.translateService.instant('toolbar.about'),
                        url: '/screen/about',
                    },
                ],
            }
        );

        this.sideBarMenuItems.set(sideBarMenuItems);
    }

    private updatePageTitleFromRoute(): void {
        const labelKey = this.getDeepestRouteLabel(this.router.routerState.snapshot.root);

        if (!labelKey) {
            this.titleService.setTitle('RASFF Window');
            return;
        }

        const title = this.translateService.instant(labelKey);
        this.titleService.setTitle(`RASFF Window - ${title}`);
    }

    private getDeepestRouteLabel(route: ActivatedRoute['snapshot']): string | undefined {
        let currentRoute = route;
        let label: string | undefined;

        while (currentRoute) {
            if (currentRoute.data?.label) {
                label = currentRoute.data.label;
            }
            currentRoute = currentRoute.firstChild;
        }

        return label;
    }

    private getAnnouncements() {
        this.fetchAnnouncements()
            .pipe(take(1))
            .subscribe({
                next: (announcements) => {
                    this.announcements.set(announcements.response);
                },
            });
    }

    private fetchAnnouncements() {
        return this.noticeApiService.searchActiveAnnouncements(System.RASFF_WINDOW).pipe(
            catchError((error) => {
                console.error('Error fetching announcements', error);
                return of({ response: [] });
            })
        );
    }

    // notify user that session expired
    private _checkSessionExpired() {
        const params = this.route.snapshot.queryParams;
        if (params['session_expired'] === 'true' && !this.logged) {
            this.growlService.growl(
                {
                    severity: 'warning',
                    summary: 'Session Expired',
                    detail: 'Please log in again to continue accessing restricted data',
                },
                true,
                true,
                undefined,
                'bottom-right'
            );
        }
    }

    private createMenuItem(
        label: string,
        url: string,
        iconSvgName: string
    ): { label: string; url: string; iconSvgName: string } {
        return { label, url, iconSvgName };
    }

    private setLink() {
        this.logoutLink = this.logoutUrl + this.appAuthService.convertToPublicUrl(this.router.url);
        this.loginLink =
            this.loginUrl + this.appAuthService.convertToRestrictedUrl(this.router.url);
    }

    private renderWebTools(config) {
        this.subs.push(
            this.webtoolsService.loadLoadJs().subscribe({
                next: () => {
                    if (
                        window.$wt &&
                        $wt?.analytics?.isTrackable?.() &&
                        typeof $wt?.trackPageView === 'function'
                    ) {
                        $wt.trackPageView();
                    }
                },
                error: (error) => console.error('no loadJs script', error),
            })
        );

        this.webtoolsService.loadWebToolWidget({
            utility: 'cck',
            url: WebtoolsService.config.cckUrl,
        });

        if (config.analyticsSiteID && config.analyticsSitePath && config.analyticsInstance) {
            this.webtoolsService.loadWebToolWidget({
                utility: 'analytics',
                siteID: config.analyticsSiteID,
                sitePath: config.analyticsSitePath,
                instance: config.analyticsInstance,
                mode: config.analyticsMode,
            });
        }
    }
}
